import { useEffect, useRef, useState, type ReactNode } from "react";

import {
  useGetV1MePreferences,
  usePutV1MePreferences,
  type UserPreferences,
} from "@solarkey/api-client";
import { Banner, Select, Term } from "@solarkey/ui";

import { useCapabilities } from "../session/capabilities";
import {
  CONTRAST_OPTIONS,
  DEFAULTS,
  DENSITY_OPTIONS,
  DISPLAY_UNIT_PRESETS,
  LANGUAGES,
  LOCALES,
  MOTION_OPTIONS,
  NOTIFICATION_DEFAULTS,
  THEME_OPTIONS,
  TIMEZONE_OPTIONS,
  type NotifRow,
  type NotifState,
} from "./Preferences.catalog";
import { BackButton } from "../shell/BackButton";

type Theme = "light" | "dark" | "system";
type Density = "comfortable" | "compact";

/* Read the live theme from the root element so we never accidentally flip on mount. */
function readLiveTheme(): Theme {
  const d = document.documentElement.dataset.theme;
  return d === "dark" ? "dark" : "light";
}

function readLiveDensity(): Density {
  const d = document.documentElement.dataset.density;
  return d === "compact" ? "compact" : "comfortable";
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "system") {
    const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
    root.dataset.theme = prefersDark ? "dark" : "light";
  } else {
    root.dataset.theme = theme;
  }
}

/* ── Notification toggle chip ──────────────────────────────────────── */

function cycleNotif(v: NotifState, allowDigest: boolean): NotifState {
  if (allowDigest) {
    if (v === "on") return "digest";
    if (v === "digest") return "off";
    return "on";
  }
  return v === "on" ? "off" : "on";
}

function NotifChip({
  value,
  allowDigest,
  onChange,
}: {
  value: NotifState;
  allowDigest?: boolean;
  onChange: (v: NotifState) => void;
}): ReactNode {
  const color =
    value === "on"
      ? "var(--sk-color-ink-1)"
      : value === "digest"
        ? "var(--sk-color-accent)"
        // "off": the muted state. Was --sk-color-ink-4, which does not exist
        // (the scale is ink-1/2/3), so it fell back to near-black and read like
        // "on". ink-3 is the intended muted shade.
        : "var(--sk-color-ink-3)";

  return (
    <button
      type="button"
      onClick={() => onChange(cycleNotif(value, !!allowDigest))}
      style={{
        background: "none",
        border: 0,
        cursor: "pointer",
        padding: "2px 6px",
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 500,
        color,
      }}
    >
      {value}
    </button>
  );
}

/* ── Read-only field ───────────────────────────────────────────────── */

function ReadonlyField({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={fieldLabelStyle}>{label}</span>
      <span style={readonlyValueStyle}>{value}</span>
    </div>
  );
}

/* ── Main component ────────────────────────────────────────────────── */

export function Preferences(): ReactNode {
  const caps = useCapabilities();
  const save = usePutV1MePreferences();
  const savedPrefs = useGetV1MePreferences();

  /* ── Display state ─────────────────────── */
  const [theme, setTheme] = useState<Theme>(readLiveTheme);
  const [density, setDensity] = useState<Density>(readLiveDensity);
  const [language, setLanguage] = useState(DEFAULTS.language);
  const matchedLocale = LOCALES.some((l) => l.key === caps.locale) ? caps.locale : DEFAULTS.locale;
  const [locale, setLocale] = useState(matchedLocale);
  const [timezone, setTimezone] = useState(DEFAULTS.timezone);
  const [displayUnits, setDisplayUnits] = useState(DEFAULTS.units);
  const [reducedMotion, setReducedMotion] = useState(DEFAULTS.motion);
  const [highContrast, setHighContrast] = useState(DEFAULTS.contrast);

  const scopeOptions = caps.scopes.map((s) => ({ key: s.id, label: s.label }));
  const [defaultScope, setDefaultScope] = useState(caps.scopes[0]?.id ?? "all");

  /* ── Notifications state ───────────────── */
  const [notifRows, setNotifRows] = useState<NotifRow[]>(NOTIFICATION_DEFAULTS);

  /* ── Feedback state ────────────────────── */
  const [saved, setSaved] = useState(false);

  /* ── Hydrate from the person's saved preferences ──────────────────────
     The screen could save (PUT) but never read back, so every refresh reset
     the table to the built-in defaults: a channel the person had changed
     looked like it reverted (e.g. "Findings · Email" always fell back to the
     default "digest"). Now we seed from GET /v1/me/preferences once it
     arrives, keeping the built-in default for any class the person has never
     set. Guarded so it runs once — it seeds the form, it never fights an
     edit the person makes afterwards or a refetch on window focus. */
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (savedPrefs.data?.status !== 200) return;
    const prefs = savedPrefs.data.data.data;
    hydratedRef.current = true;

    if (prefs.locale && LOCALES.some((l) => l.key === prefs.locale)) {
      setLocale(prefs.locale);
    }

    // Shape mirrors what onSave PUTs: classes[key] = {in_app, email, push, timing}.
    const classes = (
      prefs.notification_policy as
        | {
            classes?: Record<
              string,
              { in_app?: NotifState; email?: NotifState; push?: NotifState; timing?: string }
            >;
          }
        | undefined
    )?.classes;
    if (classes) {
      setNotifRows((prev) =>
        prev.map((r) => {
          const c = classes[r.key];
          if (!c) return r;
          return {
            ...r,
            inApp: c.in_app ?? r.inApp,
            email: c.email ?? r.email,
            push: c.push ?? r.push,
            timing: c.timing ?? r.timing,
          };
        }),
      );
    }
  }, [savedPrefs.data]);

  /* Apply theme + density live — skip the initial mount so navigating here
     does not flip the theme from what the user was already seeing. */
  const mountRef = useRef(true);
  useEffect(() => {
    if (mountRef.current) {
      mountRef.current = false;
      return;
    }
    applyTheme(theme);
    document.documentElement.dataset.density = density;
  }, [theme, density]);

  /* Clear the saved banner whenever anything changes. */
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setSaved(false);
  }, [theme, density, language, locale, timezone, displayUnits, reducedMotion, highContrast, defaultScope, notifRows]);

  function updateNotif(idx: number, field: "inApp" | "email" | "push", value: NotifState): void {
    setNotifRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  }

  function onSave(): void {
    const body: UserPreferences = {
      locale,
      /* eslint-disable grammar/no-unit-literal --
         These are the UserPreferencesUnits ENUM values in a request body, not a
         rendered unit: the contract's own vocabulary for the person's chosen
         display units. Figures are still rendered through the figure object,
         which is what the rule protects. Same reasoning as the picker catalog. */
      units: {
        energy: displayUnits.includes("mwh") ? "MWh" : "kWh",
        power: displayUnits.startsWith("mw") ? "MW" : "kW",
      },
      /* eslint-enable grammar/no-unit-literal */
      theme,
      density,
      // Persist what the person actually set, per class and per channel.
      // This used to send a fixed {cadence:"immediate", critical_only:false},
      // so every toggle on the table below was discarded on save while the
      // banner still said "Preferences saved". `notification_policy` is a free
      // JSON object in the contract, so the rows go through unchanged.
      notification_policy: {
        classes: Object.fromEntries(
          notifRows.map((r) => [
            r.key,
            { in_app: r.inApp, email: r.email, push: r.push, timing: r.timing },
          ]),
        ),
      },
    };
    save.mutate({ data: body }, { onSuccess: () => setSaved(true) });
  }

  const roleLabel = caps.role
    ? caps.role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "User";
  // Identity line: role · tenant (wireframe screen 15 "Regional manager (RK) · Customer A").
  // Tenant comes from the session, same source as the top-bar identity (TopBar AvatarMenu).
  const tenant = caps.accessible_tenants[0]?.name;
  const identityLabel = tenant ? `${roleLabel} · ${tenant}` : roleLabel;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "0 4px" }}>
      {/* ── Header ────────────────────────────────────── */}
      <header style={headerStyle}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <BackButton />
          <h2 style={titleStyle}>Preferences</h2>
        </div>
          <span className="muted" style={{ fontSize: 12 }}>{identityLabel}</span>
        </div>
        <button
          type="button"
          className="btn primary"
          onClick={onSave}
          disabled={save.isPending}
          aria-busy={save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
      </header>

      {saved ? <Banner tone="sync" onDismiss={() => setSaved(false)}>Preferences saved.</Banner> : null}
      {save.isError ? (
        <Banner tone="warning" onDismiss={() => save.reset()}>
          Couldn't save preferences. Your display choices still apply on this device — try again.
        </Banner>
      ) : null}

      {/* ── Section 1: Display ────────────────────────── */}
      <section aria-labelledby="pref-display" className="card" style={sectionStyle}>
        <SectionHead id="pref-display" title="Display" />
        <div style={gridStyle}>
          <Select
            label="Theme"
            items={THEME_OPTIONS}
            selectedKey={theme}
            onSelectionChange={(k) => setTheme(k as Theme)}
          />
          <Select
            label="Density"
            items={DENSITY_OPTIONS}
            selectedKey={density}
            onSelectionChange={(k) => setDensity(k as Density)}
          />
          {/* ── Not yet settable. Each control below is DISABLED and says why:
                the preferences contract carries locale, units, theme, density
                and notification policy only, and nothing in the app reads these
                five yet. Leaving them operable made them write-only UI — the
                choice vanished on save with no indication (Branding.tsx mk 3
                pattern: state the seam in words, disable the control). ── */}
          <Select
            label="Language"
            items={LANGUAGES}
            selectedKey={language}
            onSelectionChange={setLanguage}
            isDisabled
            description="Not settable yet — the app ships one language and the preference has no field"
          />
          <Select
            label="Locale and grouping"
            items={LOCALES}
            selectedKey={locale}
            onSelectionChange={setLocale}
            placeholder="Choose locale"
          />
          <Select
            label="Time zone shown"
            items={TIMEZONE_OPTIONS}
            selectedKey={timezone}
            onSelectionChange={setTimezone}
            isDisabled
            description="Not settable yet — timestamps render in site time; the preference has no field"
          />
          <Select
            label="Display units"
            items={DISPLAY_UNIT_PRESETS}
            selectedKey={displayUnits}
            onSelectionChange={setDisplayUnits}
          />
          <Select
            label="Reduced motion"
            items={MOTION_OPTIONS}
            selectedKey={reducedMotion}
            onSelectionChange={setReducedMotion}
            isDisabled
            description="Not settable yet — motion already follows the device setting"
          />
          <Select
            label="High contrast"
            items={CONTRAST_OPTIONS}
            selectedKey={highContrast}
            onSelectionChange={setHighContrast}
            isDisabled
            description="Not settable yet — no high-contrast token set exists"
          />
          {scopeOptions.length > 0 ? (
            <Select
              label="Default scope"
              items={scopeOptions}
              selectedKey={defaultScope}
              onSelectionChange={setDefaultScope}
              isDisabled
              description="Not settable yet — the landing scope comes from the session, not a preference"
            />
          ) : (
            <ReadonlyField label="Default scope" value="Resolved from the session scope" />
          )}
        </div>
        <p className="muted" style={{ fontSize: 11, margin: 0, lineHeight: 1.5 }}>
          Theme, density, locale and display units are saved to your account.
          The greyed controls have no field in the preferences contract yet, so
          they are shown as the gap they are rather than accepting a choice that
          would be dropped on save.
        </p>
      </section>

      {/* ── Section 2: Notifications ──────────────────── */}
      <section aria-labelledby="pref-notify" className="card" style={sectionStyle}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <SectionHead id="pref-notify" title="Notifications" />
          <span className="muted" style={{ fontSize: 11 }}>by class · immediate or digest · channels</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Class</th>
                <th style={{ ...thStyle, textAlign: "center", width: 70 }}>In app</th>
                <th style={{ ...thStyle, textAlign: "center", width: 70 }}>Email</th>
                <th style={{ ...thStyle, textAlign: "center", width: 70 }}>Push</th>
                <th style={thStyle}>Timing</th>
              </tr>
            </thead>
            <tbody>
              {notifRows.map((row, i) => (
                <tr key={row.key}>
                  <td style={tdStyle}>{row.label}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <NotifChip value={row.inApp} onChange={(v) => updateNotif(i, "inApp", v)} />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <NotifChip value={row.email} allowDigest onChange={(v) => updateNotif(i, "email", v)} />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <NotifChip value={row.push} onChange={(v) => updateNotif(i, "push", v)} />
                  </td>
                  <td style={{ ...tdStyle, color: "var(--sk-color-ink-3)", fontSize: 12 }}>{row.timing}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted" style={{ fontSize: 11, margin: 0, lineHeight: 1.5 }}>
          Saved per class and per channel with your preferences. The timing
          column is the wireframe's intended cadence, not a rule anything
          enforces yet — there is no quiet-hours or digest scheduler, so no
          quiet window is claimed here.
        </p>
      </section>

      {/* ── Section 3: Security and devices (wireframe §A6, mk 3) ──
           A security claim with no source is worse than a blank: "MFA on" read
           as a statement of fact while nothing here could see whether MFA was
           on, and "phone (last sync 12:40)" was a device that did not exist.
           Both are gone; the seam is stated instead. ── */}
      <section aria-labelledby="pref-security" className="card" style={sectionStyle}>
        <SectionHead id="pref-security" title="Security and devices" />
        <div style={gridStyle}>
          <SecurityField label="Sign-in">
            <span className="muted">
              Not reported — the sign-in method and two-factor status aren&rsquo;t
              available.
            </span>
          </SecurityField>
          <SecurityField label="Sessions">
            <span className="muted">
              Not reported — active sessions and devices aren&rsquo;t available.
            </span>
            {/* UX-REV-001 U2 / UX-007: "sign out others" is an unavailable Build-01
                action (no sessions store), so it is hidden rather than rendered as a
                disabled control that does nothing. */}
          </SecurityField>
          <SecurityField
            label={<>Default <Term tkey="pr">PR</Term> variant</>}
            value="Absolute · fixed by the KPI ruling, not a preference"
          />
        </div>
      </section>
    </div>
  );
}

/* ── Security field (bordered chip per wireframe mk 3) ────────────── */

function SecurityField({
  label,
  value,
  children,
}: {
  label: ReactNode;
  value?: string;
  children?: ReactNode;
}): ReactNode {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={fieldLabelStyle}>{label}</span>
      <div style={securityChipStyle}>
        {children ?? <span>{value}</span>}
      </div>
    </div>
  );
}

/* ── Section heading with badge ────────────────────────────────────── */

function SectionHead({ id, title }: { id: string; title: string }): ReactNode {
  return <h3 id={id} style={sectionTitleStyle}>{title}</h3>;
}

/* ── Styles ────────────────────────────────────────────────────────── */

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  fontSize: "var(--sk-type-title-size, 18px)",
  fontWeight: 600,
  margin: 0,
  lineHeight: 1.3,
  color: "var(--sk-color-ink-1)",
};

const sectionStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: 20,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  margin: 0,
  color: "var(--sk-color-ink-1)",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 16,
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--sk-color-ink-3)",
};

const readonlyValueStyle: React.CSSProperties = {
  fontSize: 13,
  color: "var(--sk-color-ink-2)",
  padding: "6px 0",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--sk-color-ink-3)",
  padding: "6px 8px",
  borderBottom: "1px solid var(--sk-color-border-1)",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "8px 8px",
  borderBottom: "1px solid var(--sk-color-border-1)",
  color: "var(--sk-color-ink-1)",
  whiteSpace: "nowrap",
};

const securityChipStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid var(--sk-color-border-2)",
  background: "var(--sk-color-surface-1)",
  fontSize: 13,
  color: "var(--sk-color-ink-2)",
  lineHeight: 1.5,
};

